import * as React from "react"

import { Skeleton } from "@/components/ui/alert"

/*
   The card an admin list page is built from: identity (icon, title, count, one line of what
   it is) and the page's main action in the header, then the search, filters, table and pager
   as its body. Users and Clients both use it so the two pages next to each other in the
   sidebar read as one application.

   The title IS the page heading (`h1`): these pages carry no separate one above the card,
   because a second "Users" over "Users 78" said the same thing twice.

   `count` is `undefined` until the list has answered, and renders as a skeleton then —
   a confident `0` in flight reads as "nobody here". A skeleton is only right while the
   count is still coming: `countFailed` says the list will not answer, and the count reads
   as a dash rather than pulsing forever over an error.
*/
export interface SectionCardProps {
  icon: React.ReactNode
  title: string
  subtitle: string
  /** Total rows behind the list; `undefined` while unknown. */
  count: number | undefined
  /** The list request failed, so `count` will stay unknown — show a dash, not a skeleton. */
  countFailed?: boolean
  /** The page's main action, e.g. "Add user". Rendered `size="sm"` by convention. */
  action?: React.ReactNode
  children: React.ReactNode
}

/*
   Sizing: the card is a flex column that may shrink (`min-h-0`) inside a height-bounded page.
   It is as tall as its content, and never taller than the space it is given — so put it in a
   `h-full flex flex-col` page, mark the region that should scroll `min-h-0 flex-1
   overflow-auto`, and everything else (header, toolbar, pager) keeps its own height. No
   `calc(100dvh - Npx)` guess about how tall the chrome around the table is.
*/
export function SectionCard({
  icon,
  title,
  subtitle,
  count,
  countFailed = false,
  action,
  children,
}: SectionCardProps): JSX.Element {
  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-surface">
      <header className="flex shrink-0 flex-wrap items-center gap-3 bg-surface-sunken/50 px-5 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <span className="grid size-7 shrink-0 place-items-center rounded-md bg-surface text-text-2 ring-1 ring-inset ring-border">
            {icon}
          </span>
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <h1 className="text-heading text-text">{title}</h1>
              {count === undefined && !countFailed ? (
                <Skeleton className="h-3 w-6 self-center" data-testid="section-card-count" />
              ) : (
                <span
                  className="font-id tabular-nums text-caption text-text-3"
                  data-testid="section-card-count"
                  title={countFailed ? "Unavailable" : undefined}
                >
                  {countFailed ? "—" : count}
                </span>
              )}
            </div>
            <p className="text-caption text-text-3">{subtitle}</p>
          </div>
        </div>
        {action && <div className="ml-auto shrink-0">{action}</div>}
      </header>
      {children}
    </section>
  )
}
